import { useEffect, useRef, useState } from 'react';
import { fetchAllDashboardOwnershipBatches } from '../Admin/ContentOwnership/contentOwnershipApi';
import {
  indexDashboardOwnership,
  isDashboardOwnershipPathEligible,
  isDashboardOwnershipRole
} from './dashboardOwnershipUtils';

const emptyOwnershipMap = () => new Map();
const EMPTY_DIRECTORY_ITEMS = Object.freeze([]);

const scheduleWhenIdle = (callback) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: 500 });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(callback, 0);
  return () => window.clearTimeout(id);
};

const mergeOwnershipPage = (current, pageItems) => {
  const next = new Map(current);
  indexDashboardOwnership(pageItems).forEach((item, key) => {
    next.set(key, item);
  });
  return next;
};

export default function useDashboardOwnership({
  role,
  enabled,
  currentPath,
  showDeletedView,
  listingReady,
  directoryItems
}) {
  const [ownershipByPath, setOwnershipByPath] = useState(emptyOwnershipMap);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipError, setOwnershipError] = useState('');
  const [ownershipTruncated, setOwnershipTruncated] = useState(false);
  const requestGenerationRef = useRef(0);
  const safeDirectoryItems = Array.isArray(directoryItems)
    ? directoryItems
    : EMPTY_DIRECTORY_ITEMS;

  const isOwnershipEligible = Boolean(
    isDashboardOwnershipRole(role)
    && enabled
    && !showDeletedView
    && isDashboardOwnershipPathEligible(currentPath)
  );

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    let active = true;
    const isCurrent = () =>
      active && requestGenerationRef.current === generation;

    setOwnershipByPath(emptyOwnershipMap());
    setOwnershipError('');
    setOwnershipTruncated(false);

    if (
      !isOwnershipEligible
      || !listingReady
      || safeDirectoryItems.length === 0
    ) {
      setOwnershipLoading(false);
      return () => {
        active = false;
      };
    }

    setOwnershipLoading(true);

    const loadOwnership = async () => {
      const result = await fetchAllDashboardOwnershipBatches({
        path: currentPath,
        items: safeDirectoryItems.map((item) => ({
          directory: item.directory,
          isFile: item.isFile
        })),
        shouldContinue: isCurrent,
        onBatch: (batchItems) => {
          if (isCurrent()) {
            setOwnershipByPath((current) =>
              mergeOwnershipPage(current, batchItems)
            );
          }
        }
      });

      if (!isCurrent() || result.code === 'CANCELLED') {
        return;
      }

      if (result.ok) {
        setOwnershipByPath(indexDashboardOwnership(result.data?.items));
        setOwnershipTruncated(Boolean(result.data?.truncated));
        return;
      }

      setOwnershipByPath(indexDashboardOwnership(result.partialItems));
      setOwnershipError(
        result.message || 'Content ownership lookup is unavailable.'
      );
    };

    const cancelScheduledLoad = scheduleWhenIdle(() => {
      loadOwnership().finally(() => {
        if (isCurrent()) {
          setOwnershipLoading(false);
        }
      });
    });

    return () => {
      active = false;
      cancelScheduledLoad();
      if (requestGenerationRef.current === generation) {
        requestGenerationRef.current += 1;
      }
    };
  }, [currentPath, isOwnershipEligible, listingReady, safeDirectoryItems]);

  return {
    isOwnershipEligible,
    ownershipByPath,
    ownershipLoading,
    ownershipError,
    ownershipTruncated
  };
}
